import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";

// Indikativni izračun odškodnine za prometne nesreče.
// Razponi temeljijo na sodni praksi za nepremoženjsko škodo v RS (informativno).

const INJURY_CATEGORIES = [
  {
    id: "first_visit",
    label: "Prvi obisk zdravnika / bolnišnice",
    desc: "Brez ugotovljenih resnih poškodb",
    min: 0,
    max: 1000,
  },
  {
    id: "mild",
    label: "Lažje klasične diagnoze",
    desc: "Pretres možganov, udarnine, zvini, rane, distorzije",
    min: 1200,
    max: 14500,
  },
  {
    id: "simple_fracture",
    label: "Klasičen zlom / počenost",
    desc: "Zaceli se v 1–2 mesecih",
    min: 4000,
    max: 15000,
  },
  {
    id: "complex_fracture",
    label: "Zakomplicirani ali kompresijski zlom",
    desc: "Vključno z natrganimi mišicami",
    min: 8000,
    max: 25000,
  },
  {
    id: "multiple_fractures",
    label: "Večkratni / večfragmentalni zlomi",
    desc: "Težje poškodbe mišic ali živca",
    min: 15000,
    max: 75000,
  },
  {
    id: "nerve_damage",
    label: "Odlom ali poškodba živca",
    desc: "S trajnimi posledicami",
    min: 2000,
    max: 5000,
  },
  {
    id: "severe",
    label: "Zelo hude kompleksne poškodbe",
    desc: "Politrauma, trajna invalidnost",
    min: 100000,
    max: 500000,
  },
];

export default function AutoLiabilityCalculator() {
  const [categoryId, setCategoryId] = useState("mild");
  const category = INJURY_CATEGORIES.find((c) => c.id === categoryId);
  const isFixed = category.min === category.max;
  const [amount, setAmount] = useState(Math.round((category.min + category.max) / 2));

  // Ko uporabnik zamenja kategorijo, resetiramo znesek na sredino razpona
  const selectCategory = (c) => {
    setCategoryId(c.id);
    setAmount(Math.round((c.min + c.max) / 2));
  };

  const result = useMemo(() => {
    const injury = isFixed ? category.min : amount;
    return { injury, total: injury };
  }, [category, amount, isFixed]);

  const fmt = (n) =>
    new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

  return (
    <div data-testid="auto-calculator" className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3 space-y-7">
        <div>
          <Label className="text-sm font-semibold">Jakost telesnih poškodb</Label>
          <div className="mt-3 grid sm:grid-cols-2 gap-2.5" data-testid="auto-categories">
            {INJURY_CATEGORIES.map((c) => {
              const active = c.id === categoryId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectCategory(c)}
                  data-testid={`auto-cat-${c.id}`}
                  className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                    active
                      ? "border-[var(--vp-primary)] bg-[var(--vp-primary)]/5"
                      : "border-[var(--vp-border)] bg-white hover:border-[var(--vp-primary)]/40"
                  }`}
                >
                  <div className="font-semibold text-sm">{c.label}</div>
                  <div className="text-xs text-[var(--vp-muted)] mt-0.5">{c.desc}</div>
                  <div className="text-xs font-semibold text-[var(--vp-primary)] mt-1.5">
                    {c.min === c.max
                      ? `najmanj ${fmt(c.min)}`
                      : `${fmt(c.min)} – ${fmt(c.max)}`}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {!isFixed && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold">Ocenjen znesek v razponu</Label>
              <span className="font-display font-bold text-lg">{fmt(amount)}</span>
            </div>
            <Slider
              data-testid="auto-amount-slider"
              value={[amount]}
              min={category.min}
              max={category.max}
              step={Math.max(100, Math.round((category.max - category.min) / 100))}
              onValueChange={(v) => setAmount(v[0])}
            />
            <div className="flex justify-between text-xs text-[var(--vp-muted)] mt-2">
              <span>{fmt(category.min)}</span>
              <span>{fmt(category.max)}</span>
            </div>
          </div>
        )}

        <div>
          <p className="text-xs text-[var(--vp-muted)] leading-relaxed">
            Izračun je <strong>informativen</strong> in temelji na sodni praksi za
            nepremoženjsko škodo. Dejanska odškodnina je odvisna od medicinske
            dokumentacije in pogajanj z zavarovalnico.
          </p>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div
          className="rounded-2xl p-7 h-full flex flex-col border border-[var(--vp-border)]"
          style={{ background: "var(--vp-sand)" }}
        >
          <div className="text-sm text-[var(--vp-muted)]">Indikativna skupna odškodnina</div>
          <div
            data-testid="auto-result"
            className="font-display font-bold text-4xl lg:text-5xl mt-2 tracking-tight text-[var(--vp-primary)]"
          >
            {fmt(result.total)}
          </div>
          <div className="mt-6 space-y-2.5 text-sm">
            <Row label="Telesne poškodbe" value={fmt(result.injury)} />
          </div>

          <div className="mt-auto pt-7 space-y-2.5">
            <a
              href="#dokumenti"
              data-testid="auto-cta"
              className="inline-flex w-full items-center justify-center rounded-full text-white font-semibold px-6 py-3"
              style={{ background: "var(--vp-primary)" }}
            >
              Prijavi primer →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-[var(--vp-border)] pb-2">
      <span className="text-[var(--vp-muted)]">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
